import PropTypes from 'prop-types';

import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import React from 'react';
import Select, { components as builtInComponents, createFilter } from 'react-select';
import AsyncSelect from 'react-select/async';
import styled from 'styled-components';

import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';

const NoOptionsMessage = props => (
  <NoOptionsMessageWrapper>
    <Typography color="textSecondary" {...props.innerProps}>
      {props.children}
    </Typography>
  </NoOptionsMessageWrapper>
);

NoOptionsMessage.propTypes = {
  children: PropTypes.any,
  innerProps: PropTypes.any,
};

const NoOptionsMessageWrapper = styled.div`
  padding: 15px 20px;
  color: red;
`;

const Option = props => (
  <Tooltip title={props.selectProps.showLabelTooltip ? props.label : ''} placement="right">
    <MenuItem
      style={
        props.selectProps.isCustomStyled
          ? { backgroundColor: props.data.color ? props.data.color : '#fff' }
          : {}
      }
      buttonRef={props.innerRef}
      component="div"
      selected={props.isFocused}
      {...props.innerProps}
    >
      {props.children}
    </MenuItem>
  </Tooltip>
);

Option.propTypes = {
  children: PropTypes.object,
  innerProps: PropTypes.object,
  innerRef: PropTypes.any,
  isFocused: PropTypes.bool,
  data: PropTypes.object,
  selectProps: PropTypes.object,
  label: PropTypes.any,
};

const InputContainer = ({ children }) => (
  <InputContainerWrapper>{children}</InputContainerWrapper>
);

InputContainer.propTypes = {
  children: PropTypes.object,
};

const InputContainerWrapper = styled.div`
  > div > label {
    transform: none;
    top: ${({ isMulti }) => (isMulti ? '6px' : '12px')};
    font-size: 10px;
  }

  > div > div {
    margin-top: 9px;
  }

  > div > div > div {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    position: relative;
    padding-bottom: 0;
    height: auto;
  }

  > div > div > div :last-child {
    cursor: pointer;
  }
`;

const inputComponent = ({ inputRef, ...props }) => (
  <div ref={inputRef} {...props} />
);

inputComponent.propTypes = {
  inputRef: PropTypes.any,
};

const Control = props => (
  <InputContainerWrapper isMulti={props.selectProps.isMulti}>
    <TextField
      fullWidth
      label={props.selectProps.label}
      {...props.selectProps.textFieldProps}
      InputProps={{
        inputComponent,
        inputProps: {
          inputRef: props.innerRef,
          children: props.children,
          ...props.innerProps,
        },
      }}
    />
  </InputContainerWrapper>
);

Control.propTypes = {
  children: PropTypes.object,
  InputProps: PropTypes.any,
  innerProps: PropTypes.any,
  innerRef: PropTypes.any,
  selectProps: PropTypes.any,
};

Control.defaultProps = {
  InputProps: {},
};

const components = {
  Option,
  Control,
  NoOptionsMessage,
};

const itemHeight = 35;

const MenuList = props => {
  const { options, children, getValue } = props;
  const [value] = getValue();
  const initialOffset = children.length < 8 ? 0 : options.indexOf(value) * itemHeight;
  const maxHeight = Math.min(children.length * itemHeight + 15, 300);

  return (
    <div style={{ height: maxHeight }}>
      <AutoSizer>
        {props => {
          const { width, height } = props;
          return (
            <List
              height={height}
              width={width}
              itemCount={children.length}
              itemSize={itemHeight}
              initialScrollOffset={initialOffset}
            >
              {props => {
                const { index, style } = props;
                return <div style={style}>{children[index]}</div>;
              }}
            </List>
          );
        }}
      </AutoSizer>
    </div>
  );
};

MenuList.propTypes = {
  options: PropTypes.string,
  maxHeight: PropTypes.number,
  getValue: PropTypes.any,
  children: PropTypes.object,
  index: PropTypes.number,
  style: PropTypes.object,
  width: PropTypes.number,
  height: PropTypes.number,
};

const MultiValue = props => (
  <builtInComponents.MultiValue {...props}>
    {props.data.value}
  </builtInComponents.MultiValue>
);

MultiValue.propTypes = {
  data: PropTypes.any,
};

const filterEmpty = options => options.filter(o => o);

const getOptions = (addEmptyOption, options) => {
  if (!addEmptyOption) return options;
  if (typeof options[0] === 'object') {
    return [{ value: ' ', label: '- No Value -' }, ...options];
  } else if (typeof options[0] === 'string') {
    return ['', ...options];
  } else if (typeof options[0] === 'number') {
    return [null, ...options];
  }
};

const CustomSelect = ({
  styles,
  addEmptyOption = true,
  isCustomStyled,
  customStyles,
  async = false,
  handleLarge = false,
  disabled,
  options = [],
  label = '',
  loadOptions = null,
  placeholder,
  controlShouldRenderValue = true,
  input: { onChange, value },
  onDropdownFocus,
  innerRef,
  isMulti = false,
  needCustomChip = false,
  showLabelTooltip = false,
}) => {
  const validOptions = filterEmpty(options);
  return (
    <DropdownInputContainer style={{ ...styles }}>
      {async ? (
        <AsyncSelect
          isMulti={isMulti}
          components={components}
          controlShouldRenderValue={controlShouldRenderValue}
          isDisabled={disabled}
          label={label}
          loadOptions={loadOptions}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onFocus={onDropdownFocus}
          ref={innerRef}
          styles={customStyles}
          isCustomStyled={isCustomStyled}
        />
      ) : (
        <Select
          isMulti={isMulti}
          components={{
            ...components,
            ...(handleLarge && { MenuList }),
            ...(needCustomChip && { MultiValue }),
          }}
          controlShouldRenderValue={controlShouldRenderValue}
          isDisabled={disabled}
          label={label}
          options={getOptions(addEmptyOption, validOptions)}
          placeholder={placeholder}
          value={
            typeof value === 'string' || typeof value === 'number'
              ? options.find(o => value === o.value)
              : value
          }
          onChange={onChange}
          onFocus={onDropdownFocus}
          ref={innerRef}
          styles={customStyles}
          isCustomStyled={isCustomStyled}
          showLabelTooltip={showLabelTooltip}
          filterOption={createFilter({ ignoreAccents: false })}
        />
      )}
    </DropdownInputContainer>
  );
};

CustomSelect.propTypes = {
  addEmptyOption: PropTypes.bool,
  async: PropTypes.bool,
  handleLarge: PropTypes.bool,
  controlShouldRenderValue: PropTypes.bool,
  disabled: PropTypes.bool,
  input: PropTypes.object,
  label: PropTypes.string,
  loadOptions: PropTypes.any,
  options: PropTypes.any,
  placeholder: PropTypes.string,
  onDropdownFocus: PropTypes.any,
  styles: PropTypes.object,
  innerRef: PropTypes.object,
  isMulti: PropTypes.bool,
  isCustomStyled: PropTypes.bool,
  customStyles: PropTypes.object,
  needCustomChip: PropTypes.bool,
  showLabelTooltip: PropTypes.bool,
};

const DropdownInputContainer = styled.div`
  > div > div + div {
    z-index: 2;
  }
`;

export default CustomSelect;
